import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TextField from '@mui/material/TextField';
import { debounce } from '@mui/material/utils';

import { Button } from 'common/components/Button';

interface IEmailTextFieldProps {
    messageRef: React.RefObject<string>;
    onClearRef: React.RefObject<(() => void) | null>;
    onHasMessageChange: (hasMessage: boolean) => void;
}

const EmailTextField = ({
    messageRef,
    onClearRef,
    onHasMessageChange,
}: IEmailTextFieldProps) => {
    const [translate] = useTranslation();
    const [localValue, setLocalValue] = useState('');

    onClearRef.current = useCallback(() => {
        setLocalValue('');
    }, []);

    const debouncedHasMessageChange = useMemo(
        () =>
            debounce(
                (value: string) => onHasMessageChange(!!value.trim()),
                500
            ),
        [onHasMessageChange]
    );

    const handleChange = (newValue: string) => {
        setLocalValue(newValue);
        messageRef.current = newValue;
        debouncedHasMessageChange(newValue);
    };

    return (
        <TextField
            fullWidth
            multiline
            label={translate('emailMessage')}
            minRows={3}
            sx={{ mt: 2, mb: 1, bgcolor: 'background.paper' }}
            value={localValue}
            onChange={(e) => handleChange(e.target.value)}
        />
    );
};

interface IProps {
    loading?: boolean;
    selectedUserIds: string[];
    onSendEmail: (message: string, userIds: string[]) => Promise<void>;
}

export const UserSelectFooterAppend = ({
    loading,
    selectedUserIds,
    onSendEmail,
}: IProps) => {
    const [translate] = useTranslation();
    const [hasMessage, setHasMessage] = useState(false);
    const messageRef = useRef('');
    const onClearRef = useRef<(() => void) | null>(null);

    const handleSend = useCallback(async () => {
        try {
            await onSendEmail(messageRef.current, selectedUserIds);
        } catch {
            // Error is handled by onSendEmail (showApolloError)
            return;
        }

        messageRef.current = '';
        setHasMessage(false);
        onClearRef.current?.();
    }, [onSendEmail, selectedUserIds]);

    return (
        <>
            <EmailTextField
                messageRef={messageRef}
                onClearRef={onClearRef}
                onHasMessageChange={setHasMessage}
            />

            <Button
                color="primary"
                disabled={!hasMessage || !selectedUserIds.length}
                loading={loading}
                sx={{ mt: 1 }}
                variant="contained"
                onClick={handleSend}
            >
                {translate('offerEventDetail.inviteEmail.buttonText')}
            </Button>
        </>
    );
};
